import { action, computed, IReactionDisposer, observable, reaction } from 'mobx';

import { FormStore } from '@estee/elc-form';
import { IFormValues } from '@estee/elc-form/typings/views/Form';
import { ViewController } from '@estee/elc-service';

import { IDropdownOption, ISelect } from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { DropdownDataStore, ICascadingDropdownOption } from '~repositories/DropdownDataStore';

interface IDropDownViewController {
    dropdownDataStore: DropdownDataStore;
    configRepository: ConfigRepository;
}

export class DropDownViewController extends ViewController {
    private dropdownDataStore: DropdownDataStore;
    private configRepository: ConfigRepository;

    private name: string;
    private selectLabel?: string;
    private staticLookupName?: string;
    private dependsOnField?: string;
    private pin?: string[];
    private additionalFormValues?: string[];
    private withFloatingLabel?: boolean;

    @observable private fixedOptions?: IDropdownOption[];
    @observable private formStore: FormStore<IFormValues>;
    @observable private options: IDropdownOption[] = [];

    private disposer?: IReactionDisposer;

    constructor({ dropdownDataStore, configRepository }: IDropDownViewController) {
        super();
        this.dropdownDataStore = dropdownDataStore;
        this.configRepository = configRepository;
    }

    @action
    public setFixedOptions = (options: IDropdownOption[]) => {
        this.fixedOptions = options;
    };

    @action
    public parseOptions = () => {
        const staticOptions: IDropdownOption[] | undefined = this.staticLookupName
            ? this.dropdownDataStore.getStaticOptions(this.staticLookupName, this.dependsOnField)
            : undefined;
        const rawOptions = this.fixedOptions ?? staticOptions ?? [];
        const pinnedOptions = rawOptions.filter((option) =>
            this.pin?.includes(option.value as string)
        );
        // Use vanilla js instead, since pincer-iam removed Lodash dependency to increase performance
        const difference = (raw: IDropdownOption[], pinned: IDropdownOption[]) =>
            raw.filter((option) => !pinned.includes(option));

        const options: IDropdownOption[] = [
            ...pinnedOptions,
            ...difference(rawOptions, pinnedOptions)
        ];
        if (options.length === 1) {
            this.formStore?.setFieldValue(this.name, options[0].value);
        }
        const fieldValue = this.formStore?.values[this.name];
        if (
            fieldValue !== undefined &&
            options.length &&
            !options.some(({ value }) => value === fieldValue)
        ) {
            this.formStore?.setFieldValue(this.name, '');
        }

        if (this.selectLabel && !this.withFloatingLabel) {
            options.splice(0, 0, {
                label: this.selectLabel,
                value: ''
            });
        }

        this.options = options;
    };

    @computed
    public get data() {
        return {
            options: this.options
        };
    }

    @action
    public loaded = async ({
        name,
        selectLabel,
        formStore
    }: {
        name: string;
        selectLabel?: string;
        formStore: FormStore<IFormValues>;
    }) => {
        this.name = name;
        this.selectLabel = selectLabel;
        this.formStore = formStore;
        const {
            staticLookupName,
            dependsOnField,
            additionalFormValues,
            pin,
            withFloatingLabel
        } = (this.configRepository.getFieldConfig(name) ?? {}) as ISelect;
        this.staticLookupName = staticLookupName;
        this.dependsOnField = dependsOnField;
        this.additionalFormValues = additionalFormValues;
        this.withFloatingLabel = withFloatingLabel;
        this.pin = pin;
        if (staticLookupName) {
            await this.dropdownDataStore.loadStaticLookupData(staticLookupName);
        }
        this.disposer = reaction(
            () => {
                const {
                    staticLookupDataCollection: staticLookupJsonCollection,
                    getParentPath
                } = this.dropdownDataStore;

                return {
                    jsonData: staticLookupName
                        ? staticLookupJsonCollection.get(staticLookupName)
                        : undefined,
                    parentPath: staticLookupName
                        ? getParentPath(staticLookupName, dependsOnField)
                        : undefined,
                    fixedOptions: this.fixedOptions
                };
            },
            () => this.parseOptions(),
            {
                equals: (prev, next) => {
                    return (
                        prev.jsonData === next.jsonData &&
                        prev.parentPath === next.parentPath &&
                        prev.fixedOptions === next.fixedOptions
                    );
                }
            }
        );
        this.parseOptions();
    };

    @action
    public afterChange = (fieldName: string, value: string | number) => {
        if (this.staticLookupName) {
            const { setCurrentPath } = this.dropdownDataStore;
            const option = this.findOptionByValue(this.options, value);
            // Update path so fields depends on this can easily get updated.
            if (option) {
                setCurrentPath(this.staticLookupName, fieldName, option.path);
                this.additionalFormValues?.forEach((otherFieldName) => {
                    this.formStore.setFieldValue(otherFieldName, option[otherFieldName] as string);
                });
            }
        }
    };

    public dispose = () => {
        this.disposer?.();
    };

    private findOptionByValue(options: IDropdownOption[], value: string | number) {
        return options.reduce(
            (option, { path, value: val, ...rest }: ICascadingDropdownOption, currentIndex) =>
                val === value
                    ? ({
                          ...rest,
                          path: path?.length === 0 ? [currentIndex] : path
                      } as ICascadingDropdownOption)
                    : option,
            undefined
        );
    }
}
